import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import GenericTable from '../general/generic-table';
import ExecutionPipeline, { useJobRunStates } from './execution-pipeline';
import { utilService } from "../../services/util-service"
import { httpService } from "../../services/http-service"
import { newHttpService } from "../../services/new-http-service"
import { useCachedFetch } from "../../hooks/use-cached-fetch"
import { systemHasSchedule } from "../../services/schedule-utils"
import { updateAuditControlMutation } from "../util-cmps/graphql-mutations/update-audit-control"
import { isRequiredSystem, SystemTypes } from "../forms/is-required-system"
import { StoreDispatch } from "../../store/configure-store"
import { setUserMsg } from "../../store/user"


interface AuditControlTableProps {
  data: any;
  companyName: string;
  lastSuccessfulRuns: Record<number, string>;
  onDataChange: () => void;
  onEditClick?: (auditControl: any) => void;
  onCloneClick?: (auditControl: any) => void;
  onBatchEditConsolidation?: (auditControls: any[]) => void;
  onlyProblematic?: boolean;
}

const getHealthStatus = (lastRunIso: string | undefined): { severity: 'green' | 'orange' | 'red'; label: string; sortKey: number; tooltip: string } => {
  if (!lastRunIso) return { severity: 'red', label: 'Never', sortKey: -1, tooltip: 'No successful run recorded' }

  const normalized = lastRunIso.endsWith('Z') || lastRunIso.includes('+') ? lastRunIso : lastRunIso + 'Z'
  const lastRun = new Date(normalized)
  if (isNaN(lastRun.getTime())) return { severity: 'red', label: 'Never', sortKey: -1, tooltip: 'No successful run recorded' }

  const tooltip = utilService.formatDateTime(lastRunIso)
  const now = new Date()
  const diffMs = now.getTime() - lastRun.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  // sortKey = timestamp in seconds — higher = more recent = healthier
  const sortKey = Math.floor(lastRun.getTime() / 1000)

  if (diffDays <= 1) return { severity: 'green', label: 'Today', sortKey, tooltip }
  if (diffDays <= 2) return { severity: 'orange', label: 'Yesterday', sortKey, tooltip }
  if (diffDays <= 3) return { severity: 'orange', label: `${Math.floor(diffDays)} days ago`, sortKey, tooltip }
  return { severity: 'red', label: `${Math.floor(diffDays)} days ago`, sortKey, tooltip }
}

const getMissingSystems = (item: any): string[] => {
  const objNum = item.control?.objective?.objectiveNumber ?? null
  if (objNum === null) return []
  const missing: string[] = []
  if (isRequiredSystem(objNum, SystemTypes.IN_SCOPE) && !item.control?.mainSystem) missing.push('Main System')
  if (isRequiredSystem(objNum, SystemTypes.HR) && !item.control?.hrSystem) missing.push('HR System')
  if (isRequiredSystem(objNum, SystemTypes.TICKET) && !item.control?.ticketSystem) missing.push('Ticket System')
  return missing
}

const preprocessData = (data: any[], lastSuccessfulRuns: Record<number, string>) => {
  return data.map((item: any) => {
    const firstName = item.controlOwner?.firstName || ''
    const lastName = item.controlOwner?.lastName || ''
    const controlOwnerName = `${firstName} ${lastName}`.trim()
    const health = getHealthStatus(lastSuccessfulRuns[item.id])
    const missingSystems = getMissingSystems(item)
    return { ...item, controlOwnerName, healthStatus: health, missingSystems }
  })
}

const createMapping = (title: string, location: string[], type: string, trueText?: string, falseText?: string) => ({
  title, location, type, trueText, falseText
})

const mapping = [
  createMapping('ID', ['id'], 'string'),
  createMapping('Entity Name', ['audit', 'entity', 'name'], 'string'),
  createMapping('Objective Name', ['control', 'objective', 'name'], 'string'),
  createMapping('Main System', ['control', 'mainSystem', 'system', 'name'], 'string'),
  createMapping('HR System', ['control', 'hrSystem', 'system', 'name'], 'string'),
  createMapping('Ticket System', ['control', 'ticketSystem', 'system', 'name'], 'string'),
  createMapping('Status', ['status'], 'string'),
  createMapping('Control Owner', ['controlOwnerName'], 'string'),
  createMapping('Health', ['healthStatus'], 'health'),
  createMapping('Visible', ['isHidden'], 'boolean', 'No', 'Yes'),
  createMapping('Active', ['isAutomated'], 'boolean', 'Yes', 'No'),
]

// Functional component to render the array of objects
const AuditControlTable: React.FC<AuditControlTableProps> = ({ data, companyName, lastSuccessfulRuns, onDataChange, onEditClick, onCloneClick, onBatchEditConsolidation, onlyProblematic }) => {

  const dispatch = useDispatch<StoreDispatch>()
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(new Set())

  const { data: glueSchedules } = useCachedFetch('glue-triggers', async () => {
    const result = await newHttpService.get('/api/backoffice/glue-schedules')
    return result.schedules as any[]
  })

  // Local overrides for optimistic updates (avoids full page reload on toggle)
  const [localOverrides, setLocalOverrides] = useState<Record<number, { isAutomated?: boolean; isHidden?: boolean }>>({})

  const allProcessedData = useMemo(() => {
    const base = preprocessData(data, lastSuccessfulRuns)
    if (Object.keys(localOverrides).length === 0) return base
    return base.map((item: any) => {
      const override = localOverrides[Number(item.id)]
      return override ? { ...item, ...override } : item
    })
  }, [data, lastSuccessfulRuns, localOverrides])
  const processedData = useMemo(() => {
    if (!onlyProblematic) return allProcessedData
    return allProcessedData.filter((item: any) => item.healthStatus.severity !== 'green')
  }, [allProcessedData, onlyProblematic])

  const tableData = useMemo(() => {
    const result: any = []
    for (let index = 0; index < processedData.length; index++) {
      const item = processedData[index]
      const itemData = []
      for (let i = 0; i < mapping.length; i++) {
        const cell: any = {
          title: mapping[i].title,
          value: utilService.getCellValue(item, mapping[i]),
          type: mapping[i].type,
        }
        if (mapping[i].title === 'ID') {
          const warnings: string[] = []
          if (item.missingSystems?.length > 0) {
            warnings.push(`Missing systems: ${item.missingSystems.join(', ')}`)
          }
          if (glueSchedules) {
            const entityName = item.audit?.entity?.name || ''
            const unscheduled: string[] = []
            for (const key of ['mainSystem', 'hrSystem', 'ticketSystem']) {
              const sys = item.control?.[key]?.system
              if (sys && entityName && !systemHasSchedule(entityName, sys.name, sys.abbreviation, glueSchedules)) {
                unscheduled.push(sys.name)
              }
            }
            if (unscheduled.length > 0) {
              warnings.push(`No ETL schedule: ${unscheduled.join(', ')}`)
            }
          }
          if (warnings.length > 0) {
            cell.partialTooltip = warnings.join('\n')
          }
        }
        itemData.push(cell)
      }
      result.push(itemData)
    }
    return result
  }, [processedData, glueSchedules])

  const jobRunStates = useJobRunStates()

  const getRowClassName = useCallback((rowData: any[]) => {
    const idCell = rowData.find((cell: any) => cell.title === 'ID')
    if (!idCell) return ''
    const status = jobRunStates.get(String(idCell.value))
    if (status === 'running') return 'row-etl-running'
    if (status === 'success') return 'row-etl-success'
    if (status === 'error') return 'row-etl-error'
    return ''
  }, [jobRunStates])

  const renderExpandedRow = useCallback((rowData: any[]) => {
    const auditIdCell = rowData.find((cell: any) => cell.title === 'ID')
    const auditControlId = auditIdCell?.value || ''
    const rawItem = processedData.find((item: any) => Number(item.id) === Number(auditControlId))
    const missing = rawItem?.missingSystems || []
    const entityName = rawItem?.audit?.entity?.name || ''
    const unscheduledSystems: string[] = []
    if (glueSchedules && entityName) {
      for (const key of ['mainSystem', 'hrSystem', 'ticketSystem']) {
        const sys = rawItem?.control?.[key]?.system
        if (sys && !systemHasSchedule(entityName, sys.name, sys.abbreviation, glueSchedules)) {
          unscheduledSystems.push(sys.name)
        }
      }
    }
    return <>
      {missing.length > 0 && (
        <div className="partial-warning partial-warning-expanded">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>Partial setup — missing required system{missing.length > 1 ? 's' : ''}: <strong>{missing.join(', ')}</strong></span>
        </div>
      )}
      {unscheduledSystems.length > 0 && (
        <div className="schedule-warning schedule-warning-expanded">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>No ETL schedule configured for: <strong>{unscheduledSystems.join(', ')}</strong>.<br /><span style={{ display: 'inline-block', marginTop: '0.3rem' }}>If this is not a manual control, add a schedule in the ETL Schedules page or you won't be able to trigger the ETL.</span></span>
        </div>
      )}
      <ExecutionPipeline auditControlId={auditControlId} auditControl={rawItem} companyName={companyName} onDataChange={onDataChange}
        siblingAcIds={(() => {
          const entityName = rawItem?.audit?.entity?.name
          const systemIds = new Set([
            rawItem?.control?.mainSystem?.id,
            rawItem?.control?.hrSystem?.id,
            rawItem?.control?.ticketSystem?.id,
          ].filter(Boolean))
          return processedData
            .filter((ac: any) => {
              if (ac.id === rawItem?.id) return false
              if (ac.audit?.entity?.name !== entityName) return false
              return [ac.control?.mainSystem?.id, ac.control?.hrSystem?.id, ac.control?.ticketSystem?.id]
                .some(id => id && systemIds.has(id))
            })
            .map((ac: any) => String(ac.id))
        })()}
      />
    </>
  }, [processedData, glueSchedules, onDataChange])

  const updateAuditControl = (auditControlId: number, fields: { isHidden?: boolean; isAutomated?: boolean }) => {
    // Optimistic update — reflect change immediately in local state
    setLocalOverrides(prev => ({ ...prev, [auditControlId]: { ...prev[auditControlId], ...fields } }))

    const query = updateAuditControlMutation()
    const variables = { auditControlId, ...fields }
    httpService.graphql(companyName, { query, variables }).catch(() => {
      // Revert on failure and notify user
      setLocalOverrides(prev => {
        const next = { ...prev }
        delete next[auditControlId]
        return next
      })
      dispatch(setUserMsg({ msg: { type: 'danger', txt: `Failed to update audit control ${auditControlId}` } }))
      onDataChange()
    })
  }

  const handleEditClick = useCallback((rowData: any[]) => {
    if (!onEditClick) return
    const auditIdCell = rowData.find((cell: any) => cell.title === 'ID')
    const auditControlId = Number(auditIdCell?.value)
    if (!auditControlId) return
    const rawItem = processedData.find((item: any) => Number(item.id) === auditControlId)
    if (rawItem) onEditClick(rawItem)
  }, [processedData, onEditClick])

  const handleCloneClick = useCallback((rowData: any[]) => {
    if (!onCloneClick) return
    const auditIdCell = rowData.find((cell: any) => cell.title === 'ID')
    const auditControlId = Number(auditIdCell?.value)
    if (!auditControlId) return
    const rawItem = processedData.find((item: any) => Number(item.id) === auditControlId)
    if (rawItem) onCloneClick(rawItem)
  }, [processedData, onCloneClick])

  const onCellClick = (rowData: any[], _rowIndex: number, cellTitle: string) => {
    const auditIdCell = rowData.find((cell: any) => cell.title === 'ID')
    const auditControlId = Number(auditIdCell?.value)
    if (!auditControlId) return

    const rawItem = processedData.find((item: any) => Number(item.id) === auditControlId)
    if (!rawItem) return

    if (cellTitle === 'Visible') {
      const newValue = !rawItem.isHidden
      const action = newValue ? 'hide' : 'unhide'
      if (window.confirm(`Are you sure you want to ${action} audit control ${auditControlId}?`)) {
        updateAuditControl(auditControlId, { isHidden: newValue })
      }
    }

    if (cellTitle === 'Active') {
      const newValue = !rawItem.isAutomated
      const action = newValue ? 'activate' : 'deactivate'
      if (window.confirm(`Are you sure you want to ${action} audit control ${auditControlId}?`)) {
        updateAuditControl(auditControlId, { isAutomated: newValue })
      }
    }
  }

  const handleBatchEditClick = () => {
    if (!onBatchEditConsolidation) return
    const selected = Array.from(selectedRowIndices).map(idx => processedData[idx]).filter(Boolean)
    if (selected.length > 0) onBatchEditConsolidation(selected)
  }

  return (<>
    <GenericTable
      data={tableData}
      recordName="Audit Controls"
      searchPlaceholder="Search ID, Objective, or Owner..."
      renderExpandedRow={renderExpandedRow}
      onCellClick={onCellClick}
      onEditClick={onEditClick ? handleEditClick : undefined}
      onCloneClick={onCloneClick ? handleCloneClick : undefined}
      editColumn="ID"
      enableRowSelection={!!onBatchEditConsolidation}
      selectedRows={selectedRowIndices}
      onSelectionChange={setSelectedRowIndices}
      filterableColumns={['Entity Name', 'Objective Name', 'Main System', 'HR System', 'Ticket System', 'Status', 'Health', 'Control Owner', 'Visible', 'Active']}
      getRowClassName={getRowClassName}
    />
    {selectedRowIndices.size > 0 && onBatchEditConsolidation && (
      <div className="batch-toolbar">
        <span className="batch-toolbar-count">{selectedRowIndices.size} selected</span>
        <button className="batch-toolbar-btn" onClick={handleBatchEditClick}>Batch Edit Consolidation</button>
        <button className="batch-toolbar-clear" onClick={() => setSelectedRowIndices(new Set())}>Clear</button>
      </div>
    )}
  </>);
};

export default AuditControlTable;
